import { QueryKey, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import { CountryCode } from '@rover/constants/src/geography/geo.constants';
import { Locale } from '@rover/constants/src/i18n/lingui.constants';
import type { CountryConfiguration } from '@rover/rsdk/src/apiClient/latest/model/countryConfiguration';
import type { CountryConfigurationUrls } from '@rover/rsdk/src/apiClient/latest/model/countryConfigurationUrls';
import type { PaginatedCountryConfigurationList } from '@rover/rsdk/src/apiClient/latest/model/paginatedCountryConfigurationList';

import { useApiCountryConfigurationCurrentRetrieve } from '../../../apiClient/latest/api-country-configuration-current-retrieve';
import { useApiCountryConfigurationList } from '../../../apiClient/latest/api-country-configuration-list';
import { useApiCountryConfigurationNavigationRetrieve } from '../../../apiClient/latest/api-country-configuration-navigation-retrieve';
import { ErrorType } from '../../Network/orvalHTTPInterface';

const METERS_IN_MILE = 1609.34;
const METERS_IN_KM = 1000;

// UTILS //

export const isEU = (configuration?: CountryConfiguration): boolean => {
  if (!configuration) return false;
  return configuration?.continentCode === 'EU';
};

export const convertLocalizedToMeters = (
  value: number,
  configuration?: CountryConfiguration
): number => {
  if (!configuration || configuration.distanceUnit === 'mi') {
    return Math.round(value * METERS_IN_MILE);
  }
  if (configuration.distanceUnit === 'km') {
    return value * METERS_IN_KM;
  }
  return value;
};

export const convertMetersToLocalized = (
  value: number,
  configuration?: CountryConfiguration
): number => {
  // Round values to nearest 10th, accounting for floating point errors
  if (!configuration || configuration.distanceUnit === 'mi') {
    return Math.round((value / METERS_IN_MILE + Number.EPSILON) * 10) / 10;
  }
  if (configuration.distanceUnit === 'km') {
    return Math.round((value / METERS_IN_KM + Number.EPSILON) * 10) / 10;
  }
  return value;
};

// HOOKS //

export type UseCurrentCountryConfiguration = UseQueryResult<
  CountryConfiguration,
  ErrorType<unknown>
> & {
  queryKey: QueryKey;
  emergencyHotline?: string;
  urls?: CountryConfigurationUrls;
};

export const useCurrentCountryConfiguration = (
  queryOptions: UseQueryOptions<CountryConfiguration, ErrorType<unknown>, CountryConfiguration> = {}
): UseCurrentCountryConfiguration => {
  const query = useApiCountryConfigurationCurrentRetrieve({ query: queryOptions });
  const emergencyHotline = query.data?.phoneNumbers?.emergencyHotlinePhoneFormatted;
  const urls = query.data?.urls;
  return { ...query, emergencyHotline, urls };
};

export type UseNavigationCountryConfiguration = UseQueryResult<
  CountryConfiguration,
  ErrorType<unknown>
> & {
  queryKey: QueryKey;
  availableLanguages: Array<[Locale, string]> | undefined;
  isEU: boolean;
  urls: CountryConfigurationUrls | undefined;
};

export const useNavigationCountryConfiguration = (
  queryOptions: UseQueryOptions<CountryConfiguration, ErrorType<unknown>, CountryConfiguration> = {}
): UseNavigationCountryConfiguration => {
  const query = useApiCountryConfigurationNavigationRetrieve({ query: queryOptions });
  const availableLanguages = query.data?.availableLanguages as Array<[Locale, string]> | undefined;
  const isEurope = isEU(query.data);
  const urls = query.data?.urls;
  return { ...query, availableLanguages, isEU: isEurope, urls };
};

export type UseCountryConfigurationByCode = UseQueryResult<
  PaginatedCountryConfigurationList,
  ErrorType<unknown>
> & {
  queryKey: QueryKey;
  countryConfiguration?: CountryConfiguration;
  // True once the configurations have loaded and `countryCode` had none of its own, whether
  // or not the US default was available to stand in for it.
  isDefaultCountryConfiguration: boolean;
};

export const useCountryConfigurationByCode = (
  countryCode?: string,
  queryOptions: UseQueryOptions<
    PaginatedCountryConfigurationList,
    ErrorType<unknown>,
    PaginatedCountryConfigurationList
  > = {}
): UseCountryConfigurationByCode => {
  const query = useApiCountryConfigurationList(undefined, { query: queryOptions });
  const configurations = query.data?.results;
  const match = configurations?.find((configuration) => configuration.countryCode === countryCode);
  const defaultConfiguration = configurations?.find(
    (configuration) => configuration.countryCode === CountryCode.US
  );
  const countryConfiguration = match ?? defaultConfiguration;

  return {
    ...query,
    countryConfiguration,
    isDefaultCountryConfiguration: query.isSuccess && !match,
  };
};
